// src/features/events/types/index.ts

import type { Channel } from '@/types/models';

/**
 * Event types (eventCode values)
 */
export type EventCode =
  | 'HB'      // Heartbeat
  | 'PCO'     // Pre-check-in opened
  | 'PCF'     // Pre-check-in finished
  | 'CIO'     // Check-in opened
  | 'CIF'     // Check-in finished
  | 'KDO'     // Key dispensing opened
  | 'KDF'     // Key dispensing finished
  | 'ERR'     // Error
  | 'COO'     // Check-out opened
  | 'COF'     // Check-out finished
  | string;   // Allow other codes

/**
 * Event status
 */
export type EventStatus = 'Completed' | 'Started' | 'Error' | 'Unknown';

/**
 * Full event interface
 */
export interface Event {
  id: string;
  eventCode: EventCode;
  deviceId: string;
  hotelId: string;
  groupId?: string;
  channel: Channel;
  reservationNumber?: string;
  alternateReservationNumbers?: string[];
  status?: EventStatus;
  sectionCode?: string;
  errorCode?: string;
  metadata?: Record<string, string>;
  createdAt: string;
  receivedAt?: string;
}

/**
 * Event filters for list view
 */
export interface EventFilters {
  reservationNumber?: string;
  hotelId?: string;
  groupId?: string;
  deviceId?: string;
  eventCode?: EventCode | EventCode[];
  channel?: Channel;
  status?: EventStatus;
  dateFrom?: string;
  dateTo?: string;
  showHeartbeats?: boolean;
  // Metadata search
  metadataKey?: string;
  metadataValue?: string;
}

/**
 * Metadata search params
 */
export interface MetadataSearchParams {
  key: string;
  value: string;
  hotelId?: string;
  groupId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Event code display info
 */
export interface EventCodeInfo {
  code: EventCode;
  name: string;
  color: string;
  category: 'operation' | 'system' | 'error';
}

/**
 * Get event code display information
 */
export const getEventCodeInfo = (code: EventCode): { color: string; category: 'operation' | 'system' | 'error' } => {
  switch (code) {
    case 'HB':
      return { color: '#9e9e9e', category: 'system' };
    case 'PCO':
    case 'PCF':
      return { color: '#2196f3', category: 'operation' };
    case 'CIO':
    case 'CIF':
      return { color: '#4caf50', category: 'operation' };
    case 'KDO':
    case 'KDF':
      return { color: '#ff9800', category: 'operation' };
    case 'COO':
    case 'COF':
      return { color: '#9c27b0', category: 'operation' };
    case 'ERR':
      return { color: '#f44336', category: 'error' };
    default:
      return { color: '#607d8b', category: 'system' };
  }
};

/**
 * Event codes that are considered operations (not heartbeats)
 */
export const OPERATION_EVENT_CODES: EventCode[] = [
  'PCO', 'PCF', 'CIO', 'CIF', 'KDO', 'KDF', 'COO', 'COF', 'ERR'
];

/**
 * All known event codes, in the order production devices emit them. Covers the
 * raw feed (check-in / pre-check-in steps, section navigation, payment, key
 * delivery, operational and PMS errors, abandonment) plus the legacy short codes
 * still present in historical data.
 */
export const ALL_EVENT_CODES: EventCode[] = [
  'HB',
  // Check-in flow
  'CI_START', 'CI_SEARCH', 'CI_IDENTIFY', 'CI_SIGN', 'CI_UPSELL', 'CI_COMP',
  // Pre-check-in flow
  'PC_LINK_OPEN', 'PC_START', 'PC_SEARCH', 'PC_IDENTIFY', 'PC_SIGN', 'PC_UPSELL', 'PC_COMP',
  // Section navigation
  'SECTION_ENTER', 'SECTION_EXIT',
  // Payment
  'PM_SUCCESS', 'PM_ERROR',
  // Key delivery
  'KD_SUCCESS', 'KD_ERROR', 'KD_REBOOT',
  // Operational / errors
  'PMS_RECOVERY_FAILED', 'PMS_RESERVATION_NOT_FOUND', 'PMS_ROOM_NOT_READY', 'AB', 'ERR',
  // Legacy short codes (historical data)
  'PCO', 'PCF', 'CIO', 'CIF', 'KDO', 'KDF', 'COO', 'COF',
];
